import { useState } from "react";
import { Star, MessageSquareQuote } from "lucide-react";

export function Stars({ value, size = "h-4 w-4" }) {
  return (
    <span className="inline-flex items-center gap-0.5" aria-label={`${value} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} className={`${size} ${value >= n - 0.25 ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
      ))}
    </span>
  );
}

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "");

export default function ReviewsSection({ rating, reviews }) {
  const [showAll, setShowAll] = useState(false);
  const list = reviews || [];
  const shown = showAll ? list : list.slice(0, 5);
  return (
    <section className="mt-10 rounded-3xl border border-slate-200 bg-white p-6 sm:p-8" data-testid="reviews-section">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-orange">Reviews</p>
          <h2 className="mt-2 font-heading text-2xl font-extrabold tracking-tight text-slate-900">What customers say</h2>
        </div>
        {rating?.count > 0 ? (
          <div className="flex items-center gap-3" data-testid="reviews-summary">
            <span className="font-heading text-4xl font-extrabold text-slate-900">{rating.avg}</span>
            <div><Stars value={rating.avg} /><p className="text-xs text-slate-500">{rating.count} review{rating.count !== 1 ? "s" : ""}</p></div>
          </div>
        ) : (
          <span className="rounded-full bg-accent px-3 py-1 text-sm font-semibold text-brand" data-testid="reviews-new-badge">New on Just Laundry</span>
        )}
      </div>
      {list.length === 0 && <p className="mt-6 text-sm text-slate-500" data-testid="reviews-empty">No reviews yet — be the first to book and rate this laundry.</p>}
      <div className="mt-6 divide-y divide-slate-100">
        {shown.map((r, i) => (
          <div key={i} className="py-4" data-testid={`review-${i}`}>
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-accent text-sm font-bold text-brand">{(r.customer_name || "C").charAt(0)}</span>
                <span className="font-semibold text-slate-900">{r.customer_name}</span>
                <Stars value={r.rating} size="h-3.5 w-3.5" />
              </div>
              <span className="text-xs text-slate-400">{fmtDate(r.created_at)}</span>
            </div>
            {r.comment && <p className="mt-2 flex gap-2 text-[15px] leading-relaxed text-slate-600"><MessageSquareQuote className="mt-0.5 h-4 w-4 shrink-0 text-slate-300" />{r.comment}</p>}
          </div>
        ))}
      </div>
      {list.length > 5 && (
        <button onClick={() => setShowAll(!showAll)} className="mt-2 rounded-full border border-slate-200 px-5 py-2 text-sm font-bold text-slate-700 transition-colors hover:bg-slate-50" data-testid="reviews-show-all">
          {showAll ? "Show fewer" : `Show all ${list.length} reviews`}
        </button>
      )}
    </section>
  );
}
